import { HttpError, type DataProvider, type CreateParams, type UpdateParams } from "react-admin";
import { isAegleApiError } from "@aegle/ra-commons";

/**
 * Binds 400/422 field validation errors to react-admin form inputs from our
 * *normalized* error pipeline (#4265 AC#6).
 *
 * Our `httpClient` is wrapped with `createErrorAwareHttpClient` (AC#2), so by the
 * time a create/update rejection reaches the provider its body is already an
 * `AegleApiError` whose `fields` is an OBJECT. ra-commons' own `withFieldErrors`
 * re-runs `parseApiError` and only extracts fields from a *raw* body
 * (`errors` / `validation_errors` / `fields[]`), so it can't see that normalized
 * object and silently drops the field errors. We therefore read the normalized
 * `fields` directly and re-throw in the shape react-admin's form controller binds
 * to (`body.errors`). Anything already converted (e.g. by `withFieldErrors` on a
 * raw path) has a plain `{errors}` body — not an `AegleApiError` — so it passes
 * through untouched. (ra-commons composition gap; see PR notes / upstream issue.)
 *
 * Kept in its own module (no uuid / Clarity / config imports) so it's unit-testable
 * without pulling the whole data provider.
 */
export const bindNormalizedFieldErrors = <T extends DataProvider>(provider: T): T => {
  const wrap = (method: "create" | "update") => async (resource: string, params: CreateParams & UpdateParams) => {
    try {
      const fn = provider[method] as (r: string, p: CreateParams & UpdateParams) => Promise<unknown>;
      return await fn(resource, params);
    } catch (error) {
      const body = (error as { body?: unknown })?.body;
      if (
        isAegleApiError(body) &&
        (body.status === 400 || body.status === 422) &&
        body.fields &&
        Object.keys(body.fields).length > 0
      ) {
        throw new HttpError(body.message, body.status, { errors: body.fields });
      }
      throw error;
    }
  };
  return { ...provider, create: wrap("create") as T["create"], update: wrap("update") as T["update"] };
};
